import { RacemapColors } from '@racemap/utilities/consts/common';
import { isPolygon } from '@racemap/utilities/functions/validation';
import { centroid } from '@turf/centroid';
import type { FeatureCollection, Polygon } from 'geojson';
import type { FillPaint } from 'mapbox-gl';
import { type FC, memo, useContext, useId, useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import { MapContext } from '../BasicMap/BasicMap';
import { useStripePattern } from '../helpers/useStripePattern';
import { LabelLayer } from './LabelLayer';

export interface Zone {
  id: string;
  name: string;
  coordinates: Polygon['coordinates'];
}

export interface Props {
  id?: string;
  beforeId?: string;
  zones: Array<Zone>;
}

const paint: FillPaint = {
  'fill-opacity': 0.5,
  'fill-pattern': 'stripe-pattern',
};

export const ZonesLayer: FC<Props> = memo(function ZonesLayer({ zones, id: settedId, beforeId }) {
  const data = getZonesData(zones);
  const genId = useId();
  const id = settedId || genId;
  const { map } = useContext(MapContext);
  useStripePattern(map);

  const labelFeatures = useMemo(
    () =>
      data.features
        .filter(isPolygon)
        .filter((f) => f.geometry.coordinates[0].length > 0)
        .map((feature) => ({
          ...centroid(feature),
          properties: {
            name: feature.properties?.name,
            color: RacemapColors.DarkRed,
          },
        })),
    [data],
  );

  return (
    <>
      <Source type="geojson" data={data} id={id} key={id}>
        <Layer
          id={`layer-zone-${id}`}
          key={`layer-zone-${id}`}
          type="fill"
          paint={paint}
          beforeId={beforeId}
        />
        <Layer
          id={`layer-zone-outline-${id}`}
          key={`layer-zone-outline-${id}`}
          type="line"
          paint={{ 'line-color': RacemapColors.DarkRed, 'line-width': 2 }}
          beforeId={`layer-zone-${id}`}
        />
      </Source>
      <LabelLayer
        labelFeatures={labelFeatures}
        color={RacemapColors.DarkRed}
        // Minimum zoom level at which labels are displayed.
        // This value (12) corresponds to a specific zoom threshold for better visibility of zone labels.
        minZoom={12}
        beforeId={`layer-zone-outline-${id}`}
      />
    </>
  );
});

const getZonesData = (zones: Array<Zone>): FeatureCollection => {
  return {
    type: 'FeatureCollection',
    features: zones.map((zone) => ({
      type: 'Feature',
      id: zone.id,
      properties: {
        id: zone.id,
        name: zone.name,
      },
      geometry: {
        type: 'Polygon',
        coordinates: zone.coordinates,
      },
    })),
  };
};
